import { Search } from '@mui/icons-material';
import { Box, Card, Grid, IconButton, InputAdornment, TextField, Tooltip, CircularProgress } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form';
import { formatDateTime } from '../../utils/dateUtils';

const ChatList = ({setSelectedBookingId,selectedBookingId}) => {
    const environment = process.env.REACT_APP_ENV_MODE;
    const productionUrl = process.env.REACT_APP_CHAT_API_PROD_URL;
    const stagingUrl = process.env.REACT_APP_CHAT_API_DEV_URL;
    const chatKey = environment == 'development' ? process.env.REACT_APP_CHAT_API_KEY_DEV : process.env.REACT_APP_CHAT_API_KEY;
    
    const [messages, setMessages] = useState([]);
    const [isChatLoading, setIsChatLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [searchBookingId, setSearchBookingId] = useState('');
    const [hasMore, setHasMore] = useState(true);
    
    const observer = useRef();
    const lastChatElementRef = useCallback(node => {
        if (isLoadingMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !isChatLoading) {
                loadMoreChats();
            }
        });
        if (node) observer.current.observe(node);
    }, [isLoadingMore, hasMore, isChatLoading]);

    const fetchMessages = async (page = 1, bookingId = '', isNewSearch = false) => {
        if (page === 1) {
            setIsChatLoading(true);
        } else {
            setIsLoadingMore(true);
        }

        try {
            const params = new URLSearchParams({
                page: page.toString(),
                per_page: '8'
            });
            
            if (bookingId) {
                params.append('bookingId', bookingId);
            }

            const response = await axios.get(
                `${environment == 'development' ? stagingUrl : productionUrl}/api/getChats?${params}`, 
                {
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Access-Control-Allow-Origin': '*',
                        "X-API-Key": chatKey,
                    },
                    withCredentials: true
                }
            );

            const data = response.data;
            console.log(data);
            
            if (isNewSearch || page === 1) {
                setMessages(data.chats || []);
                // Set first booking ID as default when loading first page
                if (data.chats && data.chats.length > 0 && page === 1) {
                    setSelectedBookingId(data.chats[0].bookingId);
                }
            } else {
                setMessages(prevMessages => [...prevMessages, ...(data.chats || [])]);
            }
            
            
            setTotalPages(data.totalPages || 0);
            setCurrentPage(data.page || 1);
            setHasMore(data.page < data.totalPages);

        } catch (error) {
            console.error('Error fetching messages:', error);
            if (page === 1) {
                setMessages([]);
            }
        } finally {
            setIsChatLoading(false);
            setIsLoadingMore(false);
        }
    };

    const loadMoreChats = () => {
        if (!hasMore || isLoadingMore || isChatLoading) return;
        const nextPage = currentPage + 1;
        fetchMessages(nextPage, searchBookingId, false);
    };

    useEffect(() => {
        fetchMessages();
    }, []);

    const { control, handleSubmit, reset } = useForm();
    
    const handleSearch = async (data) => {
        const searchKey = data.searchKey?.trim() || '';
        console.log('Searching for:', searchKey);
        setSelectedBookingId(null);
        setSearchBookingId(searchKey);
        setCurrentPage(1);
        setHasMore(true);
        await fetchMessages(1, searchKey, true);
    };

    const handleClearSearch = () => {
        reset();
        setSearchBookingId('');
        setCurrentPage(1);
        setHasMore(true);
        fetchMessages(1, '', true);
    };

    const handleChatClick = (bookingId) => {
        setSelectedBookingId(bookingId);
    }

    return (
        <Box sx={{height:'100%',overflow:'hidden'}}>
            <Card sx={{ p: 1 }}>
                <Card sx={{ p: 1, fontWeight: 600, textAlign: 'center' }}>
                    All Booking Chats
                </Card>
                
                <Box component={'form'} onSubmit={handleSubmit(handleSearch)} sx={{ my: 1 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={11}>
                            <Controller
                                name="searchKey"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        placeholder="Search chat by booking ID"
                                        size="small"
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                paddingLeft: '0px',
                                            }
                                        }}
                                        InputProps={{
                                            inputProps: {
                                                style: {
                                                    textTransform: 'uppercase',
                                                    fontSize: 'small'
                                                },
                                            },
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <IconButton>
                                                        <Search sx={{ fontSize: 'small' }} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={1} display={'flex'} alignItems={'center'} justifyContent={'center'}>
                            <Tooltip title="Search">
                                <IconButton
                                    size='small'
                                    color="primary"
                                    type="submit"
                                    sx={{ border: "1px solid", borderRadius: "50%", p: .5 }}
                                >
                                    <Search sx={{ fontSize: '1rem' }} />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Box>

                {/* Clear search button */}
                {searchBookingId && (
                    <Box sx={{ mb: 1, textAlign: 'center' }}>
                        <IconButton
                            onClick={handleClearSearch}
                            size="small"
                            sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                        >
                            Clear search ({messages.length} results for "{searchBookingId}")
                        </IconButton>
                    </Box>
                )}

                {/* Chat Messages */}
                <Box sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {isChatLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                            <CircularProgress size={30} />
                        </Box>
                    ) : messages && messages.length > 0 ? (
                        <>
                            {messages.map((chat, index) => {
                                const isLastElement = messages.length === index + 1;
                                return (
                                    <Box
                                        key={`${chat.bookingId}-${index}`}
                                        ref={isLastElement ? lastChatElementRef : null}
                                        sx={{ p: 1, borderBottom: '2px solid #fff', cursor: 'pointer',
                                            bgcolor: chat.bookingId === selectedBookingId ? '#3a2bdecf' : '#f0f8ff',
                                        }}
                                        onClick={() => handleChatClick(chat.bookingId)}

                                    >
                                        <Box sx={{ fontSize: '0.75rem', fontWeight: 600 , color: chat.bookingId === selectedBookingId ? '#fff' : '#333'}}>
                                            Booking ID: {chat.bookingId}
                                        </Box>
                                        <Box sx={{ 
                                            fontSize: '0.7rem', 
                                            color: chat.bookingId === selectedBookingId ? '#fff' : '#555', 
                                            whiteSpace: 'nowrap', 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis' 
                                        }}>
                                            {chat.lastMessage}
                                        </Box>
                                        <Box sx={{ 
                                            fontSize: '0.65rem', 
                                            color: chat.bookingId === selectedBookingId ? '#fff' : '#999', 
                                            display: 'flex', 
                                            justifyContent: 'flex-end' 
                                        }}>
                                            <span>{formatDateTime(chat.createdAt)}</span>
                                        </Box>
                                    </Box>
                                );
                            })}

                            {/* Loading more indicator */}
                            {isLoadingMore && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                    <CircularProgress size={20} />
                                </Box>
                            )}

                            {/* No more messages indicator */}
                            {!hasMore && messages.length > 0 && (
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    p: 2, 
                                    fontSize: '0.8rem', 
                                    color: '#999' 
                                }}>
                                    No more chats to load
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                            {searchBookingId ? `No chats found for booking ID: ${searchBookingId}` : 'No chats available'}
                        </Box>
                    )}
                </Box>
            </Card>
        </Box>
    );
};

export default ChatList;